/**
 * DesignerElement - Draggable wrapper for canvas elements
 *
 * Uses dnd-kit's useDraggable hook to make elements movable on the grid.
 * Renders the appropriate element type (TextBlock, ImageBlock, ButtonBlock).
 */

import { memo, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { DesignerElement as DesignerElementType } from '@deep-agents/shared-types';
import { TextBlock } from './elements/TextBlock';
import { ImageBlock } from './elements/ImageBlock';
import { ButtonBlock } from './elements/ButtonBlock';
import { DEFAULT_CELL_WIDTH, DEFAULT_CELL_HEIGHT } from './constants';

interface DesignerElementProps {
  element: DesignerElementType;
  cellWidth?: number;
  cellHeight?: number;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  onUpdate?: (id: string, props: Partial<DesignerElementType['props']>) => void;
}

export const DesignerElement = memo(({
  element,
  cellWidth = DEFAULT_CELL_WIDTH,
  cellHeight = DEFAULT_CELL_HEIGHT,
  isSelected = false,
  onSelect,
  onUpdate,
}: DesignerElementProps) => {
  // dnd-kit draggable setup
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: element.id,
    data: { element },
  });

  // Calculate pixel position from grid coordinates
  const left = element.position.col * cellWidth;
  const top = element.position.row * cellHeight;

  // Calculate size in pixels
  const width = element.size.colSpan * cellWidth;
  const height = element.size.rowSpan * cellHeight;

  // Apply transform during drag
  const style = {
    position: 'absolute' as const,
    left,
    top,
    width,
    height,
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : isSelected ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  // Handle click to select
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(element.id);
  }, [element.id, onSelect]);

  // Handle prop updates from child elements
  const handleUpdate = useCallback((props: Partial<DesignerElementType['props']>) => {
    onUpdate?.(element.id, props);
  }, [element.id, onUpdate]);

  // Render the correct element type
  const renderElement = () => {
    switch (element.type) {
      case 'text':
        return <TextBlock element={element} onUpdate={handleUpdate} />;
      case 'image':
        return <ImageBlock element={element} onUpdate={handleUpdate} />;
      case 'button':
        return <ButtonBlock element={element} onUpdate={handleUpdate} />;
      default:
        return <div className="text-red-500 text-xs">Unknown element</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleClick}
      className={`
        rounded-lg overflow-hidden transition-shadow
        ${isSelected ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
        ${isDragging ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}
        bg-white
      `}
      {...listeners}
      {...attributes}
    >
      {renderElement()}
    </div>
  );
});

DesignerElement.displayName = 'DesignerElement';
